import React, { useEffect, useState } from "react";
import { NeoInput } from "../../../../../components/Common";
import { toast } from "react-toastify";
import { SWIM_SET_LIBRARY } from "../../domain/swimming/setLibrary";
import { calculateSwimSetMeters } from "../../domain/swimming/calculations";
import { useTenant } from "../../../../../hooks/useTenant";
import SwimSetLibraryService from "../../../../../services/swim_set_library_service";
import Swal from "sweetalert2";
import ConfirmDialog from "../../../../../components/Common/ConfirmDialog";

const SwimSetLibraryPicker = ({ onAddSet, refreshKey = 0 }) => {
    const { idTenant } = useTenant();
    const [customSets, setCustomSets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState("all");

    const loadCustomSets = React.useCallback(async (mounted = true) => {
        if (!idTenant) return;
        setLoading(true);
        try {
            const data = await SwimSetLibraryService.getAll(idTenant);
            if (mounted) setCustomSets(data);
        } catch (error) {
            console.error("Erro ao carregar biblioteca de sets:", error);
        } finally {
            if (mounted) setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        let mounted = true;
        loadCustomSets(mounted);
        return () => {
            mounted = false;
        };
    }, [loadCustomSets, refreshKey]);

    const items = [
        ...customSets,
        ...SWIM_SET_LIBRARY.map(item => ({ ...item, source: "system" })),
    ];

    const categories = ["all", ...Array.from(new Set(items.map(item => item.category).filter(Boolean)))];
    const visibleItems = categoryFilter === "all"
        ? items
        : items.filter(item => item.category === categoryFilter);

    const handleRename = async (event, item) => {
        event.stopPropagation();
        
        const { value: formValues } = await Swal.fire({
            title: "Editar Set",
            html: `
                <div class="text-start mb-3">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome do Set</label>
                    <input id="swal-input-name" class="form-control" placeholder="Ex: 4x50m Livre" value="${item.name || ""}">
                </div>
                <div class="text-start">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Categoria</label>
                    <input id="swal-input-category" class="form-control" placeholder="Ex: Principal, Aquecimento" value="${item.category || "Personalizado"}">
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#2196f3",
            cancelButtonColor: "#6c757d",
            preConfirm: () => {
                const name = document.getElementById("swal-input-name").value.trim();
                const category = document.getElementById("swal-input-category").value.trim();
                if (!name) {
                    Swal.showValidationMessage("O nome do set é obrigatório!");
                    return false;
                }
                return { name, category: category || "Personalizado" };
            }
        });

        if (!formValues) return;
        const { name, category } = formValues;

        try {
            await SwimSetLibraryService.rename({ idTenant, setId: item.id, name, category });
            await loadCustomSets(true);
            toast.success("Set atualizado!");
        } catch (error) {
            toast.error(error.message || "Erro ao atualizar set.");
        }
    };

    const handleDelete = async (event, item) => {
        event.stopPropagation();
        
        const confirmed = await ConfirmDialog({
            title: "Excluir Set?",
            text: `Deseja realmente remover o set "${item.name}" da sua biblioteca?`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });
        
        if (!confirmed) return;

        try {
            await SwimSetLibraryService.delete({ idTenant, setId: item.id });
            await loadCustomSets(true);
            toast.success("Set removido da biblioteca.");
        } catch (error) {
            toast.error(error.message || "Erro ao remover set.");
        }
    };

    return (
        <div className="swim-set-library">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                    <h6 className="mb-0 fw-bold font-size-13">Biblioteca de sets</h6>
                    <span className="text-muted font-size-11">Sets do sistema e personalizados</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <NeoInput
                        type="select"
                        size="sm"
                        value={categoryFilter}
                        onChange={(event) => setCategoryFilter(event.target.value)}
                        className="swim-set-library-filter"
                    >
                        {categories.map(category => (
                            <option key={category} value={category}>
                                {category === "all" ? "Todas" : category}
                            </option>
                        ))}
                    </NeoInput>
                    {loading && <i className="mdi mdi-loading mdi-spin text-primary ms-2" />}
                </div>
            </div>
            <div className="swim-set-library-grid">
                {visibleItems.map(item => {
                    const meters = calculateSwimSetMeters(item.set);
                    const isCustom = item.source === "tenant";
                    return (
                        <button
                            key={`${item.source}-${item.id}`}
                            type="button"
                            className="swim-set-library-item"
                            onClick={() => onAddSet(item.set)}
                        >
                            <span className="fw-semibold text-dark">{item.name}</span>
                            <span className="text-muted">
                                {item.category} • {meters}m • {item.source === "tenant" ? "Minha biblioteca" : "Sistema"}
                            </span>
                            {isCustom && (
                                <span className="swim-set-library-actions">
                                    <button type="button" title="Renomear set" onClick={(event) => handleRename(event, item)}>
                                        <i className="mdi mdi-pencil-outline" />
                                    </button>
                                    <button type="button" title="Excluir set" onClick={(event) => handleDelete(event, item)}>
                                        <i className="mdi mdi-trash-can-outline" />
                                    </button>
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default SwimSetLibraryPicker;
